import { eventCatalog, eventChapterOf, type EventCatalogEntry } from "./events";

/**
 * The feedback form after the September 2026 visit, at
 * vyaustralia.org/feedback. Shared so the page, the backend's validation
 * and the team app's tallies offer exactly the same choices.
 */

/** Stored with every response, so a later form can share the table. */
export const EVENT_FEEDBACK_SURVEY = "september-2026";

/** The visit ran from the first of September to the last. */
const VISIT_FROM = Date.parse("2026-09-01T00:00:00Z");
const VISIT_UNTIL = Date.parse("2026-10-01T00:00:00Z");

/** Whether a catalog event was part of the September 2026 visit. */
export function isSeptember2026Visit(entry: EventCatalogEntry): boolean {
  const start = entry.startsAt ? Date.parse(entry.startsAt) : Number.NaN;
  return start >= VISIT_FROM && start < VISIT_UNTIL;
}

/** The Australian events of the visit, in the order they happened: the
 *  form's "which did you come to" list. */
export function feedbackEvents(): EventCatalogEntry[] {
  return eventCatalog
    .filter((e) => eventChapterOf(e) === "aus" && isSeptember2026Visit(e))
    .sort((a, b) => (a.startsAt ?? "").localeCompare(b.startsAt ?? ""));
}

/** Ticked instead of an event by someone who registered but couldn't
 *  come. */
export const FEEDBACK_DID_NOT_COME = "did-not-come";

export const FEEDBACK_FEEL = [
  "Loved it",
  "Really good",
  "It was okay",
  "Not really for me",
] as const;

export const FEEDBACK_AGAIN = [
  "Yes, tell me when",
  "Maybe",
  "Probably not",
] as const;

export const ONLINE_INTEREST = ["Yes", "Maybe", "Not right now"] as const;

export const ONLINE_DAYS = [
  "Mon",
  "Tue",
  "Wed",
  "Thu",
  "Fri",
  "Sat",
  "Sun",
] as const;

/** Times of day for the online meditation, in Sydney, Melbourne and
 *  Canberra time (the three cities share a clock). */
export const ONLINE_TIME_IDS = [
  "early",
  "before-work",
  "lunch",
  "evening",
  "late",
] as const;

export type OnlineTimeId = (typeof ONLINE_TIME_IDS)[number];

export const ONLINE_TIME_LABEL = {
  early: { label: "Early morning", hours: "6 to 7 am" },
  "before-work": { label: "Before work", hours: "7 to 8 am" },
  lunch: { label: "Lunchtime", hours: "12 to 1 pm" },
  evening: { label: "Evening", hours: "7 to 8 pm" },
  late: { label: "Later evening", hours: "8 to 9 pm" },
} satisfies Record<OnlineTimeId, { label: string; hours: string }>;

/** Longest free-text answer the form accepts. */
export const FEEDBACK_TEXT_MAX = 1000;
